import { FC, useCallback, useEffect, useState } from 'react';
import { changeAvailableLanguages, changePreferredLanguageAccountCenter, changePreferredLanguageViaApi } from 'store/Language/languageThunk';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'store/store';
import { DEFAULT_LANGUAGE, languages } from 'models/constants';
import { ServicesApi } from 'services/ApiService/ServiceBoard/ServiceBoardApi';
import { SgConnectEnvironment } from 'services/SgConnect/SgConnectEnvironment';

declare global {
    interface Window {
        SGWTWidgetConfiguration: {
            environment: SgConnectEnvironment,
        }
    }
}

type UseLanguageResponse = {
    preferredLanguage: Nullable<string>;
    websiteLanguage: string;
    availableLanguages: string[];
    setPreferredLanguageAccountCenter: (language: string) => void;
    setPreferredLanguageViaApi: (language: string) => void;
};

export { type languages, ALL_LANGUAGES } from 'models/constants';
export const EN_FR_LANGUAGES: languages[] = ['fr', DEFAULT_LANGUAGE];

export interface LanguageItem {
    shortName: languages;
    name: string;
}

export const My_ACCOUNT_LANGUAGES: LanguageItem[] = [
    { shortName: DEFAULT_LANGUAGE, name: 'English' },
    { shortName: 'fr', name: 'Français' },
    { shortName: 'es', name: 'Español' },
    { shortName: 'de', name: 'Deutsch' },
    { shortName: 'it', name: 'Italian ' },
    { shortName: 'pt', name: 'Portuguese' },
    { shortName: 'ja', name: '日本語' },
    { shortName: 'zh', name: '简体中文' },
];

export const LanguageProvider: FC = () => {
    const { websiteLanguage } = useLanguage();
    const { i18n } = useTranslation();
    const contextIsLoading = useAppSelector(state => state.applicationContext.isLoading);

    useEffect(() => {
        (async () => {
            if (contextIsLoading) {
                return;
            }

            if (!i18n.getResourceBundle(websiteLanguage, 'service')) {
                const data = await ServicesApi.getServicesLanguage(websiteLanguage);
                i18n.addResources(websiteLanguage, 'service', data);
                i18n.changeLanguage(websiteLanguage);
            } else if (i18n.language !== websiteLanguage) {
                i18n.changeLanguage(websiteLanguage);
            }
        })();
    }, [contextIsLoading, websiteLanguage, i18n]);

    return null;
};

// Force use of French or English
// It also update the SGWTWidgetConfiguration.bus to one of this language if it's not already set
export const LanguageEnFrOnly: FC = () => {
    const dispatch = useAppDispatch();
    const { availableLanguages } = useLanguage();
    const [originalLanguages] = useState(availableLanguages);
    const bus = (window as any).SGWTWidgetConfiguration.bus;

    useEffect(() => {
        const busLanguage = 'global.language';
        dispatch(changeAvailableLanguages(EN_FR_LANGUAGES));
        const languageSubscription = bus.subscribe(
            busLanguage,
            (language: string | undefined) => {
                if (language && !EN_FR_LANGUAGES.includes(language as languages)) {
                    bus?.publish?.(busLanguage, DEFAULT_LANGUAGE);
                }
            },
        );

        return () => {
            dispatch(changeAvailableLanguages(originalLanguages));
            bus.unsubscribe(languageSubscription);
        };
    }, [dispatch, originalLanguages, bus]);

    return null;
};

export const useLanguage = (): UseLanguageResponse => {
    const dispatch = useAppDispatch();
    const { preferredLanguage, availableLanguages } = useAppSelector(state => ({
        preferredLanguage: state.applicationContext.context?.preferredLanguage,
        availableLanguages: state.language.availableLanguages,
    }), shallowEqual);

    const websiteLanguage = availableLanguages
        .find(x => x === preferredLanguage?.toLocaleLowerCase()) ?? DEFAULT_LANGUAGE;

    const setPreferredLanguageAccountCenter = useCallback(async (language: string) => {
        dispatch(changePreferredLanguageAccountCenter(language));
    }, [dispatch]);

    const setPreferredLanguageViaApi = useCallback(async (language: string) => {
        dispatch(changePreferredLanguageViaApi(language));
    }, [dispatch]);

    return {
        preferredLanguage,
        websiteLanguage,
        availableLanguages,
        setPreferredLanguageAccountCenter,
        setPreferredLanguageViaApi,
    };
};